import React, { useState, startTransition, useEffect } from "react";
import AuthLayout from "../../components/auth/AuthLayout";
import VerifiedGuard from "../../components/auth/VerifiedGuard";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { Package } from "../../models/Package";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAllgsm, subscribeGsm } from "../../redux/slices/gsmSlice";
import { smsService } from "../../services/smsService";

type Props = {};

const SubscribeGsm: React.FC = (props: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { gsm } = useSelector((state: RootState) => state.gsm);
  const { currentUser } = useSelector((state: RootState) => state.auth);

  const [totalPrice, setTotalPrice] = useState<number | null>(null);
  const [priceCalculated, setPriceCalculated] = useState(false);

  const [rechargeFormData, setRechargeFormData] = useState({
    quantity: "",
  });

  const handleGetPriceGsm = async () => {
    try {
      const quantity = parseFloat(rechargeFormData.quantity);
      if (isNaN(quantity) || quantity <= 0) {
        toast.error(t("subscribeGsm1"));
        return;
      }

      const response = await smsService.getUnitPrice(quantity);
      if (response.status === -1) {
        toast.error(response.message);
        return;
      }

      setTotalPrice(response.unitPrice * quantity);
      setPriceCalculated(true);
      // toast.success(t("subscribeGsm2")); 
    } catch (error) {
      toast.error(t("subscribeGsm3"));
    }
  };

  const handleRecharge = (field: string, value: any) => {
    setRechargeFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
    setPriceCalculated(false);
  };

  const handleSubscribe = async () => {
    try {
      setIsLoading(true);
      const quantity = parseFloat(rechargeFormData.quantity);
      if (!quantity || !currentUser) return;

      const data = {
        quantity: quantity,
      };

      startTransition(() => {
        dispatch(subscribeGsm(data as { quantity: number }))
          .unwrap()
          .then((result) => {
            if (result.status === -1) {
              toast.error(result.message);
            } else {
              toast.success(t("subscribeGsm4"));
              // setSelectedPackage(null);
              // navigate("/whatsapp");
            }
          })
          .catch((error: any) => {
            toast.error(`${t("subscribeGsm5")}: ${error.message}`); 
          })
          .finally(() => {
            setIsLoading(false);
          });
      });
    } catch (error: any) {
      toast.error(`${t("subscribeGsm5")}: ${error.message}`);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(getAllgsm())
      .unwrap()
      .catch((err: any) => {
        console.error(err);
        toast.error(t("subscribeGsm6"));
      });
  }, [dispatch]);

  return (
    <AuthLayout>
      <VerifiedGuard>
        <main className="nxl-container apps-container apps-email">
          <div className="nxl-content without-header nxl-full-content">
            <div className="main-content d-flex">
              <div className="content-area" data-scrollbar-target="#psScrollbarInit">
                <div className="content-area-body">
                  <div className="row mx-1">
                    <div className="col-md-12 mx-auto">
                      <div className="card stretch stretch-full">
                        <div className="card-header flex-column">
                          <div className="text-center">
                           <strong>{t("subscribeGsm7")}</strong> 
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="table-responsive">
                                <table className="table table-bordered">
                                  <thead className="table-light">
                                    <tr>
                                      <th>{t("subscribeGsm8")}</th> {/* SMS Interval */}
                                      <th>{t("subscribeGsm9")}</th> {/* Unit Price */}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {gsm
                                      ?.filter((pkg) => pkg.minQuantity !== 0)
                                      .sort((a, b) => a.minQuantity - b.minQuantity)
                                      .map((pkg, index, array) => {
                                        const min = pkg.minQuantity;
                                        const nextMin = array[index + 1]?.minQuantity;
                                        const max = nextMin ? nextMin - 1 : t("subscribeGsm10"); // and above
                                        return (
                                          <tr key={pkg.id}>
                                            <td>
                                              {max === t("subscribeGsm10")
                                                ? `${min} ${max}`
                                                : `${min} - ${max}`}
                                            </td>
                                            <td>{pkg.unitPrice} €</td>
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form">
                                <div className="card">
                                  <div className="card-header">
                                    <h5>{t("subscribeGsm11")}</h5> {/* Recharge GSM */}
                                  </div>
                                  <div className="card-body">
                                    <form
                                      onSubmit={(e) => {
                                        e.preventDefault();
                                        handleGetPriceGsm();
                                      }}
                                    >
                                      <div className="row">
                                        <div className="mb-3 col-md-12">
                                          <label htmlFor="quantity">{t("subscribeGsm12")}</label> {/* Number of SMS */}
                                          <input
                                            type="number"
                                            className="form-control"
                                            placeholder={t("subscribeGsm13")} // quantity
                                            id="quantity"
                                            min={1}
                                            value={rechargeFormData.quantity}
                                            onChange={(e) =>
                                              handleRecharge("quantity", e.target.value)
                                            }
                                          />
                                        </div>
                                        {totalPrice !== null && (
                                          <div className="mb-3 col-md-12">
                                            <label>{t("subscribeGsm14")}</label> {/* Total à payer */}
                                            <input
                                              type="text"
                                              className="form-control"
                                              value={`${totalPrice.toFixed(2)} €`}
                                              readOnly
                                            />
                                          </div>
                                        )}
                                        <div className="col-12 justify-content-end">
                                          {!priceCalculated ? (
                                            <button
                                              className="btn btn-primary"
                                              disabled={
                                                !rechargeFormData.quantity ||
                                                Number(rechargeFormData.quantity) <= 0 ||
                                                isLoading
                                              }
                                            >
                                              {isLoading && <span className="loader"></span>}{" "}
                                              {t("subscribeGsm15")}
                                            </button>
                                          ) : (
                                            <button
                                              className="btn btn-success"
                                              onClick={handleSubscribe}
                                              disabled={isLoading}
                                            >
                                              {isLoading && <span className="loader"></span>}{" "}
                                              {t("subscribeGsm16")} ({`${totalPrice?.toFixed(2)} €`})
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </form>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </VerifiedGuard>
    </AuthLayout>
  );
};

export default SubscribeGsm;
